import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, Grid, IconButton, InputAdornment, Pagination, Skeleton, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { MdAccountBalanceWallet, MdClose, MdEdit, MdGroups, MdHourglassBottom, MdSearch, MdStars, MdTrendingDown, MdTrendingUp } from 'react-icons/md';
import { getData, postData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const PAGE = 10;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const n = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return isNaN(x) ? String(d) : x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Kpi = ({ title, value, hint, icon: Icon, from, to }) => (
  <Box sx={{ flex: 1, minWidth: 190, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 23, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
      {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85 }}>{hint}</Typography>}
    </Box>
  </Box>
);

const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };

const emptyRule = { spendAmountPerPoint: '', pointValue: '', expiryMonths: '' };

const Rewards = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState('');

  // rule dialog
  const [open, setOpen] = useState(false);
  const [rule, setRule] = useState(emptyRule);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // customers
  const [cust, setCust] = useState([]);
  const [custMeta, setCustMeta] = useState({ total: 0, totalPages: 1 });
  const [custPage, setCustPage] = useState(1);
  const [custLoading, setCustLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const seq = useRef(0);

  // versions + history
  const [versions, setVersions] = useState([]);
  const [verPage, setVerPage] = useState(1);
  const [verLoading, setVerLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [histMeta, setHistMeta] = useState({ total: 0, totalPages: 1 });
  const [histPage, setHistPage] = useState(1);
  const [histLoading, setHistLoading] = useState(false);

  const loadTop = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        getData('reward/config/view', null, authToken(), false),
        getData('reward/config/summary', null, authToken(), false),
      ]);
      setConfig(c?.config || null);
      setSummary(s?.summary || null);
    } catch {
      /* the cards simply stay empty */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadTop(); }, [loadTop]);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setCustPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (tab !== 0) return;
    const mine = ++seq.current;
    setCustLoading(true);
    getData('reward/config/customers', { page: custPage, limit: PAGE, search: debounced || undefined }, authToken(), false)
      .then((res) => {
        if (mine !== seq.current) return;
        setCust(res?.customers || []);
        setCustMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      })
      .catch(() => mine === seq.current && setCust([]))
      .finally(() => mine === seq.current && setCustLoading(false));
  }, [tab, custPage, debounced]);

  useEffect(() => {
    if (tab !== 1) return;
    setVerLoading(true);
    getData('reward/config/details', null, authToken(), false)
      .then((res) => setVersions(Array.isArray(res) ? res : []))
      .catch(() => setVersions([]))
      .finally(() => setVerLoading(false));
  }, [tab, config]);

  useEffect(() => {
    if (tab !== 2) return;
    setHistLoading(true);
    getData('reward/config/log', { page: histPage, limit: PAGE }, authToken(), false)
      .then((res) => {
        setHistory(res?.logHistory || []);
        setHistMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      })
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, [tab, histPage, config]);

  const openRule = () => {
    setRule(config ? { spendAmountPerPoint: config.spendAmountPerPoint, pointValue: config.pointValue, expiryMonths: config.expiryMonths } : emptyRule);
    setErrors({});
    setFormError('');
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    const spend = Number(rule.spendAmountPerPoint);
    const val = Number(rule.pointValue);
    const exp = Number(rule.expiryMonths);
    if (!rule.spendAmountPerPoint || !(spend >= 1)) e.spendAmountPerPoint = 'Enter an amount of ₹1 or more';
    if (!rule.pointValue || !(val >= 0.01)) e.pointValue = 'Enter a value of ₹0.01 or more';
    if (!rule.expiryMonths || !Number.isInteger(exp) || exp < 1 || exp > 120) e.expiryMonths = 'Enter whole months, 1 to 120';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev) => {
    ev.preventDefault();
    setFormError('');
    if (!validate()) return;
    setSaving(true);
    try {
      await postData('reward/config/add', {
        spendAmountPerPoint: Number(rule.spendAmountPerPoint),
        pointValue: Number(rule.pointValue),
        expiryMonths: Number(rule.expiryMonths),
      }, authToken(), true);
      setOpen(false);
      setNotice('Reward rule saved. New orders now earn points under it.');
      setHistPage(1);
      await loadTop();
    } catch (err) {
      setFormError(err?.message || 'Could not save the reward rule');
    } finally {
      setSaving(false);
    }
  };

  const spend = Number(rule.spendAmountPerPoint) || 0;
  const val = Number(rule.pointValue) || 0;
  const rate = spend > 0 && val > 0 ? ((val / spend) * 100).toFixed(2) : null;
  const verSlice = versions.slice((verPage - 1) * PAGE, verPage * PAGE);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Rewards management</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Loyalty points your customers earn and spend.</Typography>
        </Box>
        <Button startIcon={config ? <MdEdit /> : <MdStars />} variant="contained" onClick={openRule} sx={yellowBtn}>{config ? 'Update reward rule' : 'Set up rewards'}</Button>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        {loading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={86} sx={{ flex: 1, minWidth: 190, borderRadius: '16px' }} />) : (
          <>
            <Kpi title="Points issued" value={n(summary?.issued)} icon={MdTrendingUp} from="#6366f1" to="#4338ca" />
            <Kpi title="Points redeemed" value={n(summary?.redeemed)} icon={MdTrendingDown} from="#22c55e" to="#15803d" />
            <Kpi title="Outstanding points" value={n(summary?.outstanding)} hint={`Worth ${inr(summary?.liability)}`} icon={MdAccountBalanceWallet} from="#f59e0b" to="#b45309" />
            <Kpi title="Expiring in 30 days" value={n(summary?.expiringSoon)} hint={`${n(summary?.expired)} already expired`} icon={MdHourglassBottom} from="#ef4444" to="#b91c1c" />
            <Kpi title="Customers with points" value={n(summary?.customers)} icon={MdGroups} from="#0ea5e9" to="#0369a1" />
          </>
        )}
      </Box>

      <Box sx={{ mb: 2.5, p: 2.5, background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid rgb(255 209 2)', borderRadius: '14px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16 }}>Current reward rule</Typography>
          {config && <Chip size="small" label={`Active${config.rewardId ? ` · ${config.rewardId}` : ''}`} sx={{ fontFamily: FONT, fontWeight: 700, background: '#dcfce7', color: '#166534' }} />}
        </Box>
        {!config ? (
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#64748b' }}>No reward rule is active, so customers are not earning points. Use “Set up rewards” to start.</Typography>
        ) : (
          <Grid container spacing={2}>
            {[
              ['Earning', `1 point per ${inr(config.spendAmountPerPoint)} spent`, '#6366f1'],
              ['Redeeming', `1 point = ${inr(config.pointValue)}`, '#16a34a'],
              ['Expiry', `Points expire after ${config.expiryMonths} month${Number(config.expiryMonths) === 1 ? '' : 's'}`, '#dc2626'],
              ['Effective return', `${((config.pointValue / config.spendAmountPerPoint) * 100).toFixed(2)}% of spend`, '#f59e0b'],
            ].map(([t, v, c]) => (
              <Grid item xs={12} sm={6} md={3} key={t}>
                <Box sx={{ p: 1.75, borderRadius: '12px', background: `${c}12`, border: `1px solid ${c}33` }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 14.5, fontWeight: 800, color: '#0f172a' }}>{v}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 1.5, '& .MuiTab-root': { textTransform: 'none', fontFamily: FONT, fontWeight: 700 } }}>
          <Tab label="Customer balances" />
          <Tab label="Rule versions" />
          <Tab label="Change history" />
        </Tabs>

        {tab === 0 && (
          <>
            <Box sx={{ p: 1.75 }}>
              <TextField size="small" placeholder="Search name, mobile, email or customer ID…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: { xs: '100%', sm: 380 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={head}>Customer</TableCell>
                    <TableCell sx={head} align="right">Earned</TableCell>
                    <TableCell sx={head} align="right">Redeemed</TableCell>
                    <TableCell sx={head} align="right">Balance</TableCell>
                    <TableCell sx={head}>Next expiry</TableCell>
                    <TableCell sx={head}>Last activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {custLoading && [...Array(5)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                  {!custLoading && cust.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No customers have earned points yet.</TableCell></TableRow>}
                  {!custLoading && cust.map((c) => (
                    <TableRow key={c._id} hover>
                      <TableCell sx={cell}>
                        <b>{c.name || 'Customer'}</b>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{[c.customerCode, c.mobile, c.email].filter(Boolean).join(' · ')}</Typography>
                      </TableCell>
                      <TableCell sx={cell} align="right">{n(c.earned)}</TableCell>
                      <TableCell sx={cell} align="right">{n(c.redeemed)}</TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 800, color: c.balance > 0 ? '#166534' : '#64748b' }} align="right">{n(c.balance)}{config ? <Typography component="span" sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', fontWeight: 600 }}> ({inr(c.balance * config.pointValue)})</Typography> : null}</TableCell>
                      <TableCell sx={cell}>{c.balance > 0 ? fmtDate(c.nextExpiry) : '—'}</TableCell>
                      <TableCell sx={cell}>{fmtDate(c.lastActivity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{custMeta.total} customer{custMeta.total === 1 ? '' : 's'}</Typography>
              <Pagination count={custMeta.totalPages} page={custPage} onChange={(_, p) => setCustPage(p)} shape="rounded" size="small" />
            </Box>
          </>
        )}

        {tab === 1 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={head}>Rule</TableCell>
                    <TableCell sx={head}>Earn / spend</TableCell>
                    <TableCell sx={head}>Point value</TableCell>
                    <TableCell sx={head} align="right">Issued</TableCell>
                    <TableCell sx={head} align="right">Redeemed</TableCell>
                    <TableCell sx={head} align="right">Available</TableCell>
                    <TableCell sx={head} align="right">Expired</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verLoading && [...Array(4)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                  {!verLoading && verSlice.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No points have been issued under any rule yet.</TableCell></TableRow>}
                  {!verLoading && verSlice.map((v) => (
                    <TableRow key={v.rewardId} hover>
                      <TableCell sx={{ ...cell, fontWeight: 800 }}>{v.rewardName || '—'}{config && String(config._id) === String(v.rewardId) && <Chip size="small" label="Active" sx={{ ml: 1, height: 20, fontWeight: 700, background: '#dcfce7', color: '#166534' }} />}</TableCell>
                      <TableCell sx={cell}>1 pt / {inr(v.amountForPoint)}</TableCell>
                      <TableCell sx={cell}>{inr(v.valueForPoint)}</TableCell>
                      <TableCell sx={cell} align="right">{n(v.totalPoints)}</TableCell>
                      <TableCell sx={cell} align="right">{n(v.totalRedeemed)}</TableCell>
                      <TableCell sx={cell} align="right">{n(v.totalAvailable)}</TableCell>
                      <TableCell sx={cell} align="right">{n(v.totalExpired)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, borderTop: '1px solid #e2e8f0' }}>
              <Pagination count={Math.max(1, Math.ceil(versions.length / PAGE))} page={verPage} onChange={(_, p) => setVerPage(p)} shape="rounded" size="small" />
            </Box>
          </>
        )}

        {tab === 2 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={head}>When</TableCell>
                    <TableCell sx={head}>Change</TableCell>
                    <TableCell sx={head}>Earn / spend</TableCell>
                    <TableCell sx={head}>Point value</TableCell>
                    <TableCell sx={head}>Expiry</TableCell>
                    <TableCell sx={head}>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histLoading && [...Array(4)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                  {!histLoading && history.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No changes recorded yet.</TableCell></TableRow>}
                  {!histLoading && history.map((h, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={cell}>{fmtDate(h.date)}</TableCell>
                      <TableCell sx={cell}>{h.action || '—'}</TableCell>
                      <TableCell sx={cell}>{h.spendAmountPerPoint != null ? inr(h.spendAmountPerPoint) : '—'}</TableCell>
                      <TableCell sx={cell}>{h.pointValue != null ? inr(h.pointValue) : '—'}</TableCell>
                      <TableCell sx={cell}>{h.expiryMonths != null ? `${h.expiryMonths} mo` : '—'}</TableCell>
                      <TableCell sx={cell}>{h.user || 'Unknown'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{histMeta.total} change{histMeta.total === 1 ? '' : 's'}</Typography>
              <Pagination count={histMeta.totalPages} page={histPage} onChange={(_, p) => setHistPage(p)} shape="rounded" size="small" />
            </Box>
          </>
        )}
      </Box>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{config ? 'Update reward rule' : 'Set up rewards'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>How customers earn, spend and lose points.</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={save} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            {config && <Alert severity="info">Saving creates a new version and ends the current one. Points customers already earned keep their original expiry.</Alert>}
            <TextField fullWidth size="small" type="number" label="Spend per 1 point (₹) *" value={rule.spendAmountPerPoint} onChange={(e) => setRule({ ...rule, spendAmountPerPoint: e.target.value })}
              error={!!errors.spendAmountPerPoint} helperText={errors.spendAmountPerPoint || 'Customer earns 1 point for every this much spent'} inputProps={{ min: 1 }} />
            <TextField fullWidth size="small" type="number" label="Value of 1 point (₹) *" value={rule.pointValue} onChange={(e) => setRule({ ...rule, pointValue: e.target.value })}
              error={!!errors.pointValue} helperText={errors.pointValue || 'Discount a customer gets per point redeemed'} inputProps={{ min: 0.01, step: 0.01 }} />
            <TextField fullWidth size="small" type="number" label="Points expire after (months) *" value={rule.expiryMonths} onChange={(e) => setRule({ ...rule, expiryMonths: e.target.value })}
              error={!!errors.expiryMonths} helperText={errors.expiryMonths || 'Counted from the day the points are earned'} inputProps={{ min: 1, max: 120 }} />
            <Box sx={{ p: 2, borderRadius: '12px', background: '#fffbeb', border: '1px solid #fde68a' }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, mb: 0.5 }}>Preview</Typography>
              {rate ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#475569', lineHeight: 1.7 }}>
                  A ₹1,000 order earns <b>{Math.floor(1000 / spend)}</b> point{Math.floor(1000 / spend) === 1 ? '' : 's'}, worth <b>{inr(Math.floor(1000 / spend) * val)}</b>.
                  That is a <b>{rate}%</b> return on spend{Number(rate) > 10 ? ' — quite generous, double-check this' : ''}.
                </Typography>
              ) : <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8' }}>Fill in the rule to see an example.</Typography>}
            </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : 'Save rule'}</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Rewards;
